import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const ProjectShowcase = ({ currentLanguage }) => {
  const [selectedProject, setSelectedProject] = useState(0);

  const content = {
    es: {
      title: "Transformaciones Increíbles",
      subtitle: "Mira cómo transformamos piscinas descuidadas en oasis cristalinos",
      viewGallery: "Ver galería completa",
      before: "Antes",
      after: "Después"
    },
    en: {
      title: "Incredible Transformations",
      subtitle: "See how we transform neglected pools into crystal clear oases",
      viewGallery: "View complete gallery",
      before: "Before",
      after: "After"
    }
  };

  const projects = [
    {
      id: 1,
      titleEs: "Residencia en Escazú",
      titleEn: "Residence in Escazú",
      descriptionEs: "Recuperación completa de piscina abandonada por 6 meses",
      descriptionEn: "Complete recovery of pool abandoned for 6 months",
      beforeImage: "https://images.unsplash.com/photo-1544966503-7cc5ac882d5f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      afterImage: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      duration: "3 días",
      location: "Escazú, San José"
    },
    {
      id: 2,
      titleEs: "Hotel Boutique",
      titleEn: "Boutique Hotel",
      descriptionEs: "Mantenimiento preventivo y mejora de sistema de filtración",
      descriptionEn: "Preventive maintenance and filtration system improvement",
      beforeImage: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      afterImage: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      duration: "2 días",
      location: "Santa Ana, San José"
    },
    {
      id: 3,
      titleEs: "Condominio Residencial",
      titleEn: "Residential Condominium",
      descriptionEs: "Limpieza profunda y renovación de acabados",
      descriptionEn: "Deep cleaning and finish renovation",
      beforeImage: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      afterImage: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      duration: "5 días",
      location: "Curridabat, San José"
    }
  ];

  const text = content[currentLanguage];
  const currentProject = projects[selectedProject];

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-text-primary mb-4">
            {text.title}
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            {text.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Before/After Images */}
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              {/* Before Image */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">
                  {text.before}
                </h4>
                <div className="aspect-square rounded-lg overflow-hidden">
                  <Image
                    src={currentProject.beforeImage}
                    alt={`${text.before} - ${currentLanguage === 'es' ? currentProject.titleEs : currentProject.titleEn}`}
                    className="w-full h-full object-cover hover:scale-105 nav-transition"
                  />
                </div>
              </div>

              {/* After Image */}
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">
                  {text.after}
                </h4>
                <div className="aspect-square rounded-lg overflow-hidden">
                  <Image
                    src={currentProject.afterImage}
                    alt={`${text.after} - ${currentLanguage === 'es' ? currentProject.titleEs : currentProject.titleEn}`}
                    className="w-full h-full object-cover hover:scale-105 nav-transition"
                  />
                </div>
              </div>
            </div>

            {/* Project Navigation */}
            <div className="flex justify-center space-x-2">
              {projects.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedProject(index)}
                  className={`w-3 h-3 rounded-full nav-transition ${
                    index === selectedProject ? 'bg-primary' : 'bg-border'
                  }`}
                  aria-label={`View project ${index + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Project Details */}
          <div className="space-y-6">
            <div>
              <h3 className="text-2xl font-heading font-bold text-text-primary mb-3">
                {currentLanguage === 'es' ? currentProject.titleEs : currentProject.titleEn}
              </h3>
              <p className="text-lg text-text-secondary mb-6">
                {currentLanguage === 'es' ? currentProject.descriptionEs : currentProject.descriptionEn}
              </p>
            </div>

            {/* Project Stats */}
            <div className="grid grid-cols-2 gap-6">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Clock" size={20} className="text-primary" />
                </div>
                <div>
                  <div className="text-sm text-text-secondary">
                    {currentLanguage === 'es' ? 'Duración' : 'Duration'}
                  </div>
                  <div className="font-semibold text-text-primary">
                    {currentProject.duration}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="MapPin" size={20} className="text-primary" />
                </div>
                <div>
                  <div className="text-sm text-text-secondary">
                    {currentLanguage === 'es' ? 'Ubicación' : 'Location'}
                  </div>
                  <div className="font-semibold text-text-primary">
                    {currentProject.location}
                  </div>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-3">
              <h4 className="font-semibold text-text-primary">
                {currentLanguage === 'es' ? 'Servicios incluidos:' : 'Services included:'}
              </h4>
              <ul className="space-y-2">
                {[
                  currentLanguage === 'es' ? 'Limpieza profunda completa' : 'Complete deep cleaning',
                  currentLanguage === 'es' ? 'Balanceo químico del agua' : 'Water chemical balancing',
                  currentLanguage === 'es' ? 'Mantenimiento de equipos' : 'Equipment maintenance',
                  currentLanguage === 'es' ? 'Garantía de satisfacción' : 'Satisfaction guarantee'
                ].map((feature, index) => (
                  <li key={index} className="flex items-center space-x-2 text-text-secondary">
                    <Icon name="CheckCircle" size={16} className="text-success" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTA */}
            <Link
              to="/project-gallery"
              className="inline-flex items-center space-x-2 bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-primary/90 nav-transition"
            >
              <span>{text.viewGallery}</span>
              <Icon name="ArrowRight" size={20} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProjectShowcase;